window.CBMCourseCartUi = window.CBMCourseCartUi || {
  initialCartPromise: null,

  async parseJsonResponse(response, fallbackMessage) {
    const contentType = (response.headers.get('content-type') || '').toLowerCase();
    const raw = await response.text();

    if (!contentType.includes('application/json')) {
      console.warn('CE product form received a non-JSON response.', {
        status: response.status,
        url: response.url,
        contentType,
        preview: raw.slice(0, 140),
      });
      throw new Error(fallbackMessage);
    }

    try {
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      console.warn('CE product form received invalid JSON.', {
        status: response.status,
        url: response.url,
        preview: raw.slice(0, 140),
      });
      throw new Error(fallbackMessage);
    }
  },

  getInitialCart() {
    if (!this.initialCartPromise) {
      const root = window.Shopify?.routes?.root || '/';
      this.initialCartPromise = fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
      }).then(async (response) => {
        const cart = await this.parseJsonResponse(
          response,
          'Unable to read your cart right now.'
        );
        if (!response.ok) throw new Error('Unable to read your cart right now.');
        return cart;
      }).catch((error) => {
        this.initialCartPromise = null;
        throw error;
      });
    }

    return this.initialCartPromise;
  },
};

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        this.handleCBMCartState = this.handleCBMCartState.bind(this);
        document.addEventListener('cbm:cart-state', this.handleCBMCartState);
        this.syncInitialCartButtonState();
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Re-resolve the cart at click time so the drawer is always preferred.
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const variantId = this.form.querySelector('[name="id"]')?.value;

        // CE by Matthew courses are single-seat purchases. We also remember the
        // cart count before the add so only the first item opens the drawer.
        let previousItemCount = this.cart?.tagName === 'CART-DRAWER'
          ? this.cart.syncFloatingCartFromHeader?.() || 0
          : 0;

        if (variantId && this.cart?.tagName === 'CART-DRAWER') {
          try {
            const currentCart = await this.getCurrentCart();
            previousItemCount = Number(currentCart.item_count) || 0;
            const alreadyInCart = currentCart.items?.some(
              (item) => String(item.variant_id) === String(variantId)
            );

            if (alreadyInCart) {
              this.setAlreadyInCart(true);
              document.dispatchEvent(
                new CustomEvent('cbm:cart-state', {
                  detail: { addedVariantId: String(variantId) },
                })
              );
              return;
            }
          } catch (error) {
            console.warn('CE cart duplicate check failed:', error);
            this.handleErrorMessage('Unable to check your cart right now. Please try again.');
            return;
          }
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(async (response) => {
            const data = await window.CBMCourseCartUi.parseJsonResponse(
              response,
              'Unable to add this course right now. Please try again.'
            );
            if (!response.ok && !data.status) {
              data.status = response.status || 500;
              data.description = data.description || data.message || 'Unable to add this course right now. Please try again.';
            }
            return data;
          })
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            }
            this.error = false;
            this.setAlreadyInCart(true);
            document.dispatchEvent(
              new CustomEvent('cbm:cart-state', {
                detail: { addedVariantId: String(formData.get('id')) },
              })
            );

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response, {
                        open: previousItemCount === 0,
                        sourceElement: this.submitButton,
                      });
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response, {
                  open: previousItemCount === 0,
                  sourceElement: this.submitButton,
                });
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(e.message || 'Unable to add this course right now. Please try again.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error && this.submitButton.dataset.cbmInCart !== 'true') {
              this.submitButton.removeAttribute('aria-disabled');
            }
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      async getCurrentCart() {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, {
          headers: { Accept: 'application/json' },
        });
        const cart = await window.CBMCourseCartUi.parseJsonResponse(
          response,
          'Unable to read your cart right now.'
        );
        if (!response.ok) throw new Error('Unable to read your cart right now.');
        return cart;
      }

      async syncInitialCartButtonState() {
        try {
          const cart = await window.CBMCourseCartUi.getInitialCart();
          this.applyCartMembership(cart);
        } catch (error) {
          console.warn('CE initial cart button sync skipped:', error);
        }
      }

      handleCBMCartState(event) {
        const cart = event?.detail?.cart;
        const addedVariantId = event?.detail?.addedVariantId;
        const variantId = this.form.querySelector('[name="id"]')?.value;

        if (cart) {
          window.CBMCourseCartUi.initialCartPromise = Promise.resolve(cart);
          this.applyCartMembership(cart);
          return;
        }

        if (variantId && addedVariantId && String(variantId) === String(addedVariantId)) {
          this.setAlreadyInCart(true);
        }
      }

      applyCartMembership(cart) {
        const variantId = this.form.querySelector('[name="id"]')?.value;
        if (!variantId) return;

        const alreadyInCart = (cart?.items || []).some(
          (item) => String(item.variant_id) === String(variantId)
        );
        this.setAlreadyInCart(alreadyInCart);
      }

      setAlreadyInCart(alreadyInCart) {
        if (!this.submitButton || !this.submitButtonText) return;

        if (alreadyInCart) {
          this.submitButton.dataset.cbmInCart = 'true';
          this.submitButton.classList.add('cbm-already-in-cart');
          this.submitButton.disabled = true;
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.remove('loading');
          this.submitButtonText.textContent = 'Already in your cart';
          return;
        }

        if (this.submitButton.dataset.cbmInCart === 'true') {
          delete this.submitButton.dataset.cbmInCart;
          this.submitButton.classList.remove('cbm-already-in-cart');
          this.submitButton.disabled = false;
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      disconnectedCallback() {
        document.removeEventListener('cbm:cart-state', this.handleCBMCartState);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
